import { Component, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { COLLECTIONS, ENVIRONMENT } from '@core/core.constants';
import { Edition, PremiumPack } from '@core/models/edition.model';
import { FormInscription } from '@core/models/inscription.model';
import { AuthService } from '@core/services/auth.service';
import { CountryService } from '@core/services/country.service';
import { FirestoreService } from '@core/services/firestore.service';
import { CategoriesMasterService } from '@core/services/masters/categories-master.service';
import { UiService } from '@core/services/ui.service';
import { UserService } from '@core/services/user.service';

import { CustomSelectComponent } from '@core/components/custom-select/custom-select.component';
import { Translation, VEHICLE_BRAND_LABELS } from '@core/models/common.model';
import { EditionService } from '@core/use-case-services/edition.service';
import { InscriptionService } from '@core/use-case-services/inscription.service';
import { ModalController, NavController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import { first, firstValueFrom, Observable, shareReplay, switchMap } from 'rxjs';

@Component({
  selector: 'gt-register-form',
  templateUrl: './register-form.html',
  styles: `
    .send-button {
      --font-size: 18px;
      --font-weight: 800;
    }
    .logout {
      color: var(--ion-color-dark);
      text-decoration: underline;
      font-weight: 600;
    }
  `,
})
export class RegisterFormPage implements OnInit {
  loading = false;
  @Input() showHeader = true;
  modalOpened = false;
  hasCopilot = false;

  // Campos obligatorios de copiloto y acompañantes (el piloto los valida en el propio FormGroup)
  private readonly requiredParticipantControls = [
    'fullName',
    'nationality',
    'birthday',
    'documentNumber',
    'passport',
    'passportExpiration',
    'phone',
    'address',
    'city',
    'zipCode',
    'state',
  ];
  selectedVehicleBrandLabel = '';
  protected environment = inject(ENVIRONMENT);

  categories$ =
    this.environment.appType === 'maroc'
      ? this.categoriesMasterService.marocCategories$
      : this.categoriesMasterService.tunisieCategories$;
  countries$ = this.countryService.getCountries();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countryCallingCodes$: Observable<any[]> = this.countryService.getCountryCallingCodes();
  premiumPacks$ = this.editionService.premiumPack$;
  myCurrentInscription$ = this.inscriptionService.myCurrentInscription$.pipe(shareReplay(1));
  currentEdition$ = this.editionService.currentEdition$;
  currentLanguage$ = this.translocoService.langChanges$;
  vehicleBrands = VEHICLE_BRAND_LABELS;

  getCategoryTranslation(name: string, translations: Translation, lang: string): string {
    const translation = translations[lang as keyof Translation] || translations.en || '';
    return `${name} ${translation}`.trim();
  }

  public registerForm = new FormGroup({
    teamName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    webUrl: new FormControl(''),
    socialUrl: new FormControl(''),
    pilot: this.createParticipantFormGroup(),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    firstTime: new FormControl(null!, { nonNullable: true, validators: [Validators.required] }),
    times: new FormControl(0, { nonNullable: true, validators: [Validators.required] }),
    copilot: this.createParticipantFormGroup(false),
    participants: new FormArray([]),
    vehicleType: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    category: new FormControl<string>(null!, { nonNullable: true, validators: [Validators.required] }),
    vehicleBrand: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    otherVehicleBrand: new FormControl('', { nonNullable: true }),
    vehicleModel: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    vehicleYear: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    vehicleCC: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    vehicleCV: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    vehiclePlateNumber: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    vehicleGps: new FormControl(''),
    userComments: new FormControl('', { nonNullable: true }),
    premiumPack: new FormControl(),
    useBillingData: new FormControl(false, { nonNullable: true, validators: [Validators.required] }),
    iva: new FormControl('NO', { nonNullable: true, validators: [Validators.required] }),
    billing: new FormGroup({
      name: new FormControl('', { nonNullable: true }),
      cif: new FormControl('', { nonNullable: true }),
      address: new FormControl('', { nonNullable: true }),
      zipCode: new FormControl('', { nonNullable: true }),
      country: new FormControl('', { nonNullable: true }),
      city: new FormControl('', { nonNullable: true }),
    }),
    mailing: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
    privacy: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
    isPremium: new FormControl(false, { nonNullable: true, validators: [Validators.required] }),
  });

  public user$ = this.userService.user$.pipe(shareReplay(1));
  edition!: Edition;

  get participants(): FormArray {
    return this.registerForm.get('participants') as FormArray;
  }

  constructor(
    public navController: NavController,
    private userService: UserService,
    private uiService: UiService,
    private router: Router,
    private authService: AuthService,
    private countryService: CountryService,
    private firestore: FirestoreService,
    private inscriptionService: InscriptionService,
    private editionService: EditionService,
    private modalController: ModalController,
    private categoriesMasterService: CategoriesMasterService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit() {
    if (this.environment.platform === 'web') {
      this.authService.logout();
    }
    firstValueFrom(this.user$).then((user) => {
      if (user) {
        const pilotGroup = this.registerForm.get('pilot') as FormGroup;
        pilotGroup.patchValue({ email: user.email });
        pilotGroup.get('email')?.disable();
        pilotGroup.get('password')?.disable?.(); // In case password exists in pilot
      }
    });

    firstValueFrom(this.editionService.currentEdition$).then((edition) => (this.edition = edition));
  }

  createParticipantFormGroup(required = true) {
    const validators = required ? [Validators.required] : [];
    const emailValidators = required ? [Validators.required, Validators.email] : [Validators.email];

    return new FormGroup({
      id: new FormControl(this.firestore.generateId(COLLECTIONS.USERS), { nonNullable: true }),
      fullName: new FormControl('', { nonNullable: true, validators }),
      nationality: new FormControl('', { nonNullable: true, validators }),
      birthday: new FormControl('', { nonNullable: true, validators }),
      documentNumber: new FormControl('', { nonNullable: true, validators }),
      passport: new FormControl('', { nonNullable: true, validators }),
      passportExpiration: new FormControl('', { nonNullable: true, validators }),
      email: new FormControl('', { nonNullable: true, validators: emailValidators }),
      password: new FormControl('', { nonNullable: true, validators }),
      phone: new FormControl('', { nonNullable: true, validators }),
      dialCode: new FormControl('', { nonNullable: true, validators }),
      address: new FormControl('', { nonNullable: true, validators }),
      city: new FormControl('', { nonNullable: true, validators }),
      zipCode: new FormControl('', { nonNullable: true, validators }),
      state: new FormControl('', { nonNullable: true, validators }),
    });
  }

  get canAddParticipant(): boolean {
    return this.hasCopilot && this.participants.length < 3;
  }

  addParticipant() {
    // No se puede añadir un acompañante si antes no hay copiloto
    if (!this.hasCopilot) {
      this.uiService.showToast('ONBOARDING.REGISTER.COPILOT_REQUIRED_FIRST', 'danger', 3000);
      return;
    }
    const participants = this.registerForm.get('participants') as FormArray;
    const participantFormGroup = this.createParticipantFormGroup(false);
    this.setParticipantValidators(participantFormGroup);
    participants.push(participantFormGroup);
  }

  setParticipantValidators(participantFormGroup: FormGroup) {
    this.requiredParticipantControls.forEach((controlName: string) => {
      const formControl = participantFormGroup.get(controlName);
      if (formControl) {
        formControl.setValidators([Validators.required]);
        formControl.updateValueAndValidity();
      }
    });

    const emailControl = participantFormGroup.get('email');
    if (emailControl) {
      emailControl.setValidators([Validators.required, Validators.email]);
      emailControl.updateValueAndValidity();
    }
  }

  removeParticipant(index: number) {
    const participants = this.registerForm.get('participants') as FormArray;
    participants.removeAt(index);
  }

  setBillingValidators(active: boolean) {
    const formControlsNames = ['name', 'cif', 'address', 'zipCode', 'country', 'city'];
    formControlsNames.forEach((controlName: string) => {
      const formControl = this.registerForm.get('billing')?.get(controlName);
      if (formControl) {
        if (active) {
          formControl.setValidators([]);
        } else {
          formControl.setValidators([Validators.required]);
        }
        formControl.updateValueAndValidity();
      }
    });
  }

  setVehicleValidators(isPremium: boolean) {
    const formControlsNames = [
      'vehicleType',
      'vehicleBrand',
      'vehicleModel',
      'vehicleYear',
      'vehicleCC',
      'vehicleCV',
      'vehiclePlateNumber',
    ];
    formControlsNames.forEach((controlName: string) => {
      const formControl = this.registerForm.get(controlName);
      if (formControl) {
        if (isPremium) {
          formControl.setValidators([]);
        } else {
          formControl.setValidators([Validators.required]);
        }
        formControl.updateValueAndValidity();
      }
    });
  }

  async openNationalityModal(formControl: string | number) {
    if (this.modalOpened) return;
    this.modalOpened = true;
    const modal = await this.modalController.create({
      component: CustomSelectComponent,
      componentProps: {
        items: await firstValueFrom(this.countries$).then((countries) =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          countries.map((c: any) => ({
            value: c.cca2,
            label: c.name.common,
            flag: c.flag,
          })),
        ),
      },
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();
    if (data) {
      if (typeof formControl === 'number') {
        this.participants.at(formControl).patchValue({
          nationality: data.selectedItem,
        });
      } else {
        this.registerForm.get(formControl)?.patchValue({
          nationality: data.selectedItem as string,
        });
      }
    }
    this.modalOpened = false;
  }

  async openDialModal(formControl: string | number) {
    const modal = await this.modalController.create({
      component: CustomSelectComponent,
      componentProps: {
        items: await firstValueFrom(this.countryCallingCodes$),
        titleKey: 'DIAL_CODE',
      },
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data) {
      if (typeof formControl === 'number') {
        this.participants.at(formControl).patchValue({
          dialCode: data.selectedItem,
        });
      } else {
        this.registerForm.get(formControl)?.patchValue({
          dialCode: data.selectedItem as string,
        });
      }
    }
  }

  async openVehicleBrandModal() {
    if (this.modalOpened) return;
    this.modalOpened = true;
    const modal = await this.modalController.create({
      component: CustomSelectComponent,
      componentProps: {
        items: Object.entries(this.vehicleBrands).map(([key, label]) => ({
          value: key,
          label: label,
          image: `/assets/vehicle-brands/${key}.png`,
        })),
        titleKey: 'VEHICLE_BRAND',
      },
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();
    if (data) {
      this.registerForm.patchValue({
        vehicleBrand: data.selectedItem,
      });
      this.selectedVehicleBrandLabel = this.vehicleBrands[data.selectedItem as keyof typeof this.vehicleBrands];
    }
    this.modalOpened = false;
  }

  setCopilotValidators(hasCopilot: boolean) {
    const copilotFormGroup = this.registerForm.get('copilot');
    if (copilotFormGroup) {
      this.requiredParticipantControls.forEach((controlName: string) => {
        const formControl = copilotFormGroup.get(controlName);
        if (formControl) {
          if (hasCopilot) {
            formControl.setValidators([Validators.required]);
          } else {
            formControl.setValidators([]);
            formControl.setValue('');
          }
          formControl.updateValueAndValidity();
        }
      });

      // Handle email separately as it has different validators
      const emailControl = copilotFormGroup.get('email');
      if (emailControl) {
        if (hasCopilot) {
          emailControl.setValidators([Validators.required, Validators.email]);
        } else {
          emailControl.setValidators([Validators.email]);
          emailControl.setValue('');
        }
        emailControl.updateValueAndValidity();
      }
    }
  }

  toggleCopilot() {
    // Quitar el copiloto dejaría acompañantes huérfanos: hay que borrarlos antes
    if (this.hasCopilot && this.participants.length > 0) {
      this.uiService.showToast('ONBOARDING.REGISTER.REMOVE_PARTICIPANTS_FIRST', 'danger', 3000);
      return;
    }
    this.hasCopilot = !this.hasCopilot;
    this.setCopilotValidators(this.hasCopilot);
  }

  async confirmInscription() {
    if (this.loading) return;
    this.loading = true;
    this.registerForm.markAllAsTouched();
    if (!this.registerForm.valid) {
      this.loading = false;
      this.uiService.showToast('COMMON.WRONG_FIELDS', 'danger', 3000);
      return;
    }
    if (!this.hasCopilot && this.participants.length > 0) {
      this.loading = false;
      this.uiService.showToast('ONBOARDING.REGISTER.COPILOT_REQUIRED_FIRST', 'danger', 3000);
      return;
    }
    const formData = this.registerForm.getRawValue();

    // Solo incluir copiloto si hasCopilot es true
    if (!this.hasCopilot) {
      delete (formData as Record<string, unknown>)['copilot'];
    } else {
      // Si hasCopilot es true pero los datos están vacíos, no guardar copiloto vacío
      const copilot = formData.copilot;
      if (copilot && Object.values(copilot).every((v) => v === '' || v === null)) {
        delete (formData as Record<string, unknown>)['copilot'];
      }
    }

    await this.uiService.showLoading();
    const hasDuplicates = await this.validateDuplicatedEmails(formData);
    if (hasDuplicates) {
      this.uiService.hideLoading();
      this.uiService.showToast('COMMON.DUPLICATED_EMAILS_IN_FORM', 'danger', 3000);
      this.loading = false;
      return;
    }
    const hasDuplicatesInServer = await firstValueFrom(this.inscriptionService.validateDuplicatedEmails(formData));
    if (hasDuplicatesInServer.hasDuplicates) {
      this.uiService.hideLoading();
      this.uiService.showToast('COMMON.EMAIL_ALREADY_REGISTERED', 'danger', 3000);
      this.loading = false;
      return;
    }
    if (formData.isPremium) {
      formData.premiumPack = this.preparePremiumPack(formData.premiumPack);
    }

    const pilotPassword = formData.pilot?.password;
    if (formData.pilot && 'password' in formData.pilot) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (formData.pilot as any).password;
    }

    if (this.environment.platform === 'app') {
      this.authService.isLoggedIn$
        .pipe(
          first(),
          switchMap((isLoggedIn) => {
            if (!isLoggedIn) return this.userService.createUser(formData.pilot.email, pilotPassword);
            return Promise.resolve();
          }),
          switchMap(() =>
            this.inscriptionService.createInscriptionApp({
              ...formData,
            } satisfies Omit<FormInscription, 'id' | 'userId' | 'editionId'>),
          ),
        )
        .subscribe({
          next: () => {
            this.uiService.hideLoading();
            this.router.navigate(['/onboarding/payment'], { replaceUrl: true });
            this.loading = false;
          },
          error: (err) => {
            this.uiService.hideLoading();
            this.uiService.showToast(this.getAuthErrorLabel(err.code), 'danger', 3000);
            this.loading = false;
          },
        });
    } else if (this.environment.platform === 'web') {
      const lang = this.translocoService.getActiveLang();
      this.inscriptionService
        .createInscriptionWeb(
          {
            ...formData,
          } satisfies Omit<FormInscription, 'id' | 'userId' | 'editionId'>,
          this.environment.appType,
          pilotPassword,
          lang,
        )
        .then(async () => {
          this.uiService.hideLoading();
          await this.authService.login(formData.pilot.email, pilotPassword);
          this.router.navigate(['/onboarding/payment'], { replaceUrl: true });
          this.loading = false;
        })
        .catch((err) => {
          this.uiService.hideLoading();
          this.uiService.showToast(this.getAuthErrorLabel(err.code), 'danger', 3000);
          this.loading = false;
        });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async validateDuplicatedEmails(formData: any): Promise<boolean> {
    const participants = [
      formData.pilot,
      ...(formData.copilot ? [formData.copilot] : []),
      ...(formData.participants || []),
    ];
    const emails = participants.map((p) => p.email);
    const hasDuplicates = emails.length !== new Set(emails).size;
    if (hasDuplicates) {
      return true;
    }
    return false;
  }

  preparePremiumPack(premiumPack: PremiumPack) {
    return { id: premiumPack.id, price: premiumPack.price };
  }

  getAuthErrorLabel(error: string) {
    let errorLabel = '';
    switch (error) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
      case 'auth/invalid-login-credentials':
        errorLabel = 'LOGIN_FAILED';
        break;
      case 'auth/invalid-email':
        errorLabel = 'LOGIN_EMAIL';
        break;
      case 'auth/invalid-password':
        errorLabel = 'LOGIN_PASSWORD';
        break;
      case 'auth/weak-password':
      case 'PASSWORD_TOO_SHORT':
        errorLabel = 'PASSWORD_TOO_SHORT';
        break;
      case 'auth/email-already-exists':
      case 'auth/email-already-in-use':
        errorLabel = 'REGISTER_EXISTS';
        break;
      case 'auth/too-many-requests':
        errorLabel = 'TOO_MANY_REQ';
        break;
      case 'auth/user-disabled':
        errorLabel = 'USER_DISABLED';
        break;
      case 'auth/account-exists-with-different-credential':
        errorLabel = 'EXISTS_CREDENTIAL';
        break;
      case 'auth/popup-closed-by-user':
        errorLabel = 'POPUP_CLOSED';
        break;
      case 'auth/internal-error':
      default:
        errorLabel = 'UNKNOWN_ERROR';
        break;
    }
    return 'ONBOARDING.ERROR.' + errorLabel;
  }

  logout() {
    firstValueFrom(this.authService.logout()).then(() => {
      window.location.reload();
    });
  }

  goToLegalWebPage(slug: string) {
    const webUrl =
      this.environment.appType === 'maroc' ? 'https://www.marocchallenge.com' : 'https://www.tunisiechallenge.com';
    window.open(`${webUrl}/${slug}`, '_blank');
  }
}
